// A bot's part of its app's About tab (docs/bots.md): who made it, whether it's on, who may talk to it, the channels
// it's in and what runs it. Its owner changes all of that; server owners and admins can only switch it off (and
// take it out of channels); everyone else just reads it. The rules are shared/src/bots.ts's, the same the room uses.
import { useState } from "react";
import { accessLabel, canAddBot, canDisableBot, canEditBot, canRemoveBot, canTalkTo, type BotAccess, type BotInfo, type BotTriggerKind, type Member, type Role } from "@bloom/shared";
import type { Room } from "../realtime.ts";
import { Avatar } from "./ui.tsx";

const TRIGGER_WORDS: Record<BotTriggerKind, string> = {
  mention: "when someone @mentions it or replies to it",
  dm: "when someone messages it directly",
  message: "on messages in its channels",
  messageEdited: "on edited messages in its channels",
  reaction: "on reactions in its channels",
  reactionRemoved: "when a reaction is taken off",
  threadCreated: "when a thread starts in its channels",
  memberJoin: "when someone joins the server",
  recordChanged: "when its data changes",
  daily: "at set times of day",
  appOpen: "when someone opens its page",
};

type Who = BotAccess["who"];

export function BotAbout({ bot, room, me, members, names, channels, onMessage }: {
  bot: BotInfo;
  room: Room;
  me: { id: string; role: Role };
  members: Member[];
  names: Map<string, string>;
  /** Text channels. */
  channels: { id: string; name: string }[];
  onMessage: () => void;
}) {
  const actor = { userId: me.id, role: me.role };
  const owner = names.get(bot.ownerId) ?? "someone who left";
  const mine = canEditBot(bot, actor);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async (patch: Parameters<Room["botSettings"]>[1]) => {
    setBusy(true);
    setError(null);
    const r = await room.botSettings(bot.app, patch);
    setBusy(false);
    if (!r.ok) setError(r.message);
  };
  const setWho = (who: Who) => {
    const access: BotAccess = who === "roles" ? { who, roles: ["owner", "admin"] } : who === "people" ? { who, people: bot.access.who === "people" ? bot.access.people : [] } : { who };
    void save({ access });
  };
  const people = bot.access.who === "people" ? bot.access.people : [];
  const candidates = members.filter((m) => m.id !== bot.ownerId && !people.includes(m.id));
  const [adding, setAdding] = useState("");
  const status = bot.disabled ? (bot.pausedReason ? `Switched off: ${bot.pausedReason}` : "Switched off") : "On";
  return (
    <section className="about-card bot-about" aria-label="Bot">
      <div className="bot-about-head">
        <Avatar id={bot.id} name={bot.name} size={40} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="bot-about-name">
            {bot.name} <span className="ai-tag bot-tag">BOT</span>
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            Made by {owner} · <span className={bot.disabled ? "bot-off" : undefined}>{status}</span>
          </div>
        </div>
        {canTalkTo(bot, actor) && (
          <button type="button" className="btn btn-small btn-primary" onClick={onMessage}>
            Message {bot.name}
          </button>
        )}
      </div>

      <h4>Talks to</h4>
      {mine ? (
        <div className="bot-who" role="radiogroup" aria-label={`Who can talk to ${bot.name}`}>
          {(
            [
              ["everyone", "Everyone"],
              ["roles", "Owners and admins"],
              ["people", "People I choose"],
              ["private", "Only me"],
            ] as [Who, string][]
          ).map(([w, label]) => (
            <label key={w} className="bot-who-option">
              <input type="radio" name={`bot-who-${bot.app}`} checked={bot.access.who === w} disabled={busy} onChange={() => setWho(w)} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      ) : (
        <p className="muted">{accessLabel(bot.access, owner)}</p>
      )}
      {bot.access.who === "people" && (
        <div className="bot-people">
          {people.map((id) => (
            <span key={id} className="about-person">
              <Avatar id={id} name={names.get(id) ?? "?"} size={22} /> {names.get(id) ?? "Someone"}
              {mine && <button type="button" className="icon-btn about-remove" aria-label={`Remove ${names.get(id) ?? "them"}`} onClick={() => void save({ access: { who: "people", people: people.filter((x) => x !== id) } })}>×</button>}
            </span>
          ))}
          {mine && candidates.length > 0 && (
            <form className="about-add" onSubmit={(e) => { e.preventDefault(); if (adding) void save({ access: { who: "people", people: [...people, adding] } }).then(() => setAdding("")); }}>
              <span className="card-select">
                <select aria-label={`Add someone who can talk to ${bot.name}`} value={adding} onChange={(e) => setAdding(e.target.value)}>
                  <option value="">Add someone…</option>
                  {candidates.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </span>
              <button type="submit" className="btn btn-small" disabled={!adding || busy}>Add</button>
            </form>
          )}
        </div>
      )}
      {bot.access.who === "private" && <p className="muted about-help">Only {mine ? "you" : owner} can talk to it, in {mine ? "your" : "their"} DM with it. Its data and commands are {mine ? "yours" : "theirs"} alone.</p>}

      {bot.access.who !== "private" && (
        <>
          <h4>Channels</h4>
          <p className="muted about-help">It answers @mentions there, and its other triggers only run there.</p>
          <ul className="bot-channels">
            {channels.map((c) => {
              const on = bot.channels.includes(c.id);
              const may = on ? canRemoveBot(bot, actor) : canAddBot(bot, actor);
              if (!on && !may) return null;
              return (
                <li key={c.id}>
                  <label className="chset-app">
                    <span className="chset-app-text">
                      <span className="chset-app-name">#{c.name}</span>
                    </span>
                    <input type="checkbox" className="ss-check" aria-label={`${bot.name} in #${c.name}`} checked={on} disabled={!may || busy} onChange={(e) => void save({ channelId: c.id, on: e.target.checked })} />
                  </label>
                </li>
              );
            })}
          </ul>
          {!bot.channels.length && !canAddBot(bot, actor) && <p className="muted">Not in any channel yet.</p>}
        </>
      )}

      <h4>Runs</h4>
      <ul className="about-commands">
        {bot.triggers.map((t) => (
          <li key={t}>
            <span className="muted">{TRIGGER_WORDS[t]}</span>
          </li>
        ))}
        {!bot.triggers.length && <li className="muted">Only through its commands</li>}
      </ul>

      {canDisableBot(bot, actor) && (
        <div className="bot-switch">
          <button type="button" className="btn btn-small" disabled={busy} onClick={() => void save({ disabled: !bot.disabled })}>
            {bot.disabled ? "Turn it back on" : "Switch it off"}
          </button>
          {!mine && <span className="muted about-help">Only {owner} can change {bot.name}; owners and admins can switch it off.</span>}
        </div>
      )}
      {error && <p className="error" role="alert">{error}</p>}
    </section>
  );
}
